import type {
  ConsultationMissingItem,
  Encounter,
} from '@/features/encounters/api';
import { usePrescriptions } from '@/features/orders/api';
import type { Prescription } from '@/features/orders/types';
import type { QueueTicket } from '@/features/queue/types';
import { clinicDate } from '@/lib/clinic-time';

/** The QR on every issued document opens the public check page (/verify/<code>). */
export function verifyUrl(code: string) {
  return `${window.location.origin}/verify/${code}`;
}

/**
 * My patients today: waiting or in-room patients with no doctor yet (or this
 * doctor), and the ones this doctor has seen today. Another doctor's patients
 * are not listed.
 */
export function myPatientRows(
  tickets: QueueTicket[],
  meId: string | undefined,
) {
  return tickets.filter((t) =>
    t.status === 'waiting' || t.status === 'in_room'
      ? !t.doctor || t.doctor.id === meId
      : t.status === 'done' && t.doctor?.id === meId,
  );
}

/**
 * Client-side projection of the API signing gate. New API responses provide
 * `encounter.signing.missing`; the fallback keeps older responses safe and is
 * also updated immediately while the doctor edits the consultation.
 */
export function consultationMissing(
  encounter: Encounter,
  prescription: Prescription | null,
): ConsultationMissingItem[] {
  const missing = new Map(
    (encounter.signing?.missing ?? []).map((item) => [item.code, item]),
  );
  const set = (
    code: ConsultationMissingItem['code'],
    blocked: boolean,
    label: string,
    detail?: string,
  ) => {
    if (blocked && !missing.has(code))
      missing.set(code, { code, label, detail });
    if (!blocked) missing.delete(code);
  };

  set(
    'diagnosis',
    encounter.diagnoses.length === 0,
    'Add at least one diagnosis.',
  );
  const unconsented = encounter.procedures.filter(
    (procedure) => procedure.consent === 'needed',
  );
  set(
    'consent',
    unconsented.length > 0,
    'Record required procedure consent.',
    unconsented.map((procedure) => procedure.name).join(', ') || undefined,
  );
  set(
    'required_vitals',
    !encounter.vitals && encounter.vitalsHistory.length === 0,
    'Record at least one vital sign.',
  );

  // `undefined` means an older API did not expose registration validity. A
  // supplied null/expired value is authoritative and must block signing.
  const hasRegistrationState =
    encounter.doctor.mmcNumber !== undefined ||
    encounter.doctor.apcValidUntil !== undefined;
  if (hasRegistrationState) {
    const mmcInvalid =
      !encounter.doctor.mmcNumber ||
      !encounter.doctor.apcValidUntil ||
      encounter.doctor.apcValidUntil < clinicDate(encounter.startedAt);
    set(
      'valid_mmc_apc',
      mmcInvalid,
      'A valid MMC number and APC are required.',
    );
  }

  const rxMessages =
    prescription?.items.flatMap((item) =>
      (item.check?.messages ?? [])
        .filter((message) => message.level === 'blocked')
        .map((message) => message.text),
    ) ?? [];
  if (prescription) {
    set(
      'rx_warnings',
      !!prescription.blocked || rxMessages.length > 0,
      'Resolve blocking prescription warnings.',
      rxMessages.join('; ') || undefined,
    );
  }

  return [...missing.values()];
}

/** The live (non-cancelled) prescription of the encounter. */
export function useLivePrescription(encounterId: string) {
  const { data } = usePrescriptions(encounterId);
  return data?.find((p) => p.status !== 'cancelled') ?? null;
}

export type FormKind =
  | 'in-clinic-test'
  | 'vaccination'
  | 'phq9'
  | 'imaging'
  | 'time-slip'
  | 'attach'
  | 'adr'
  | 'child'
  | 'dv'
  | 'emergency';

export const FORM_TITLES: Record<FormKind, string> = {
  'in-clinic-test': 'In-clinic test',
  vaccination: 'Vaccination',
  phq9: 'PHQ-9',
  imaging: 'X-ray or scan',
  'time-slip': 'Time slip',
  attach: 'Attach',
  adr: 'Adverse drug reaction',
  child: 'Child protection concern',
  dv: 'Domestic violence',
  emergency: 'Emergency',
};
